import React, { useEffect, useRef } from 'react';
import { Modal } from 'components/molecules';
import { GoogleMap } from 'components/core/GoogleMap';
import PropTypes from 'prop-types';
import { compose, withStateHandlers, withHandlers } from 'recompose';
import { SearchCardInfoWindow } from 'components/search/SearchMap';
import SearchPin from 'components/search/SearchMap/SearchPin';
import './MapView.scss';

export const enhance = compose(
	withStateHandlers(
		() => ({
			facilitySelected: null,
			pinSelected: false,
		}),
		{
			handleFacilitySelected: () => (facilityId) => ({
				facilitySelected: facilityId,
			}),
			handleFacilityDeselected: () => () => ({
				facilitySelected: null,
			}),
			handlePinSelected: () => () => ({
				pinSelected: true,
			}),
			handlePinDeselected: () => () => ({
				pinSelected: false,
			}),
		},
	),
	withHandlers({
		handleSelect:
			({ handleFacilitySelected, handlePinSelected }) =>
			(facilityId) => {
				handleFacilitySelected(facilityId);
				handlePinSelected();
			},
		handleDeselect:
			({ handleFacilityDeselected, handlePinDeselected }) =>
			() => {
				handleFacilityDeselected();
				handlePinDeselected();
			},
	}),
);

export function MapView({
	isOpen,
	onClose,
	facility,
	facilities,
	facilitySelected,
	handleSelect,
	handleDeselect,
	baseUrl,
}) {
	const mapContainerRef = useRef(null);

	// 1. Default facility should be displayed when modal opens
	useEffect(() => {
		if (isOpen && facility?.id) {
			handleSelect(facility.id);
		}
	}, [isOpen, facility?.id, handleSelect]);

	// 2. Add map click listener to close info window when clicking outside
	useEffect(() => {
		const mapElement = mapContainerRef.current;
		if (mapElement && facilitySelected) {
			const handleMapClick = (e) => {
				// Check if click is outside the info window, pins, and map controls (zoom, etc.)
				if (
					!e.target.closest('.gm-style-iw') &&
					!e.target.closest('.facility-marker-button') &&
					!e.target.closest('.search-facility-marker') &&
					!e.target.closest('.gmnoprint') &&
					!e.target.closest('.gm-control-active')
				) {
					handleDeselect();
				}
			};
			mapElement.addEventListener('click', handleMapClick);
			return () => {
				mapElement.removeEventListener('click', handleMapClick);
			};
		}
	}, [facilitySelected, handleDeselect]);

	const selectedFacility = facilities.find((f) => f.id === facilitySelected);

	if (!facility?.location) return null;

	const mapTitle =
		facility.location.city && facility.location.state
			? `Map of ${facility.location.city}, ${facility.location.state}`
			: 'Map View';

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={mapTitle}
			bodyClassName="map-view-modal"
		>
			{isOpen && (
				<div
					className="map-view-two"
					ref={mapContainerRef}
				>
					<GoogleMap
						center={facility.location}
						radius={3}
					>
						{(facilities || []).map((facilityItem) => (
							<SearchPin
								key={facilityItem.id}
								facility={facilityItem}
								onClick={() => handleSelect(facilityItem.id)}
								selected={facilityItem.id === facilitySelected}
							/>
						))}
						{selectedFacility && (
							<SearchCardInfoWindow
								key={facilitySelected}
								facility={selectedFacility}
								facilitySelected={facilitySelected}
								handleFacilitySelected={handleSelect}
								handleFacilityDeselected={handleDeselect}
								handlePinDeselected={handleDeselect}
								onClose={handleDeselect}
								actions={{
									getFacilityCardPhoneNumber: () =>
										Promise.resolve({}),
								}}
								location={selectedFacility.location}
								searchType="storage"
								handleFacilityCardVisible={() => {}}
								baseUrl={baseUrl}
								inMapView
							/>
						)}
					</GoogleMap>
				</div>
			)}
		</Modal>
	);
}

MapView.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	facility: PropTypes.object,
	bodyClassName: PropTypes.string,
	facilitySelected: PropTypes.number,
	facilities: PropTypes.arrayOf(PropTypes.object),
	handleSelect: PropTypes.func.isRequired,
	handleDeselect: PropTypes.func.isRequired,
	baseUrl: PropTypes.string,
};

MapView.defaultProps = {
	facilities: [],
};

export default enhance(MapView);
